import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
  type Firestore,
} from "firebase/firestore";

export type LocationItem = {
  id: string;
  name: string;
  nameNormalized: string;
  sortOrder: number;
  isDeleted: boolean;

  countyId?: string;
  countyName?: string;
  cityId?: string;
  cityName?: string;
};

function asBool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function asNum(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : fallback;
}

function asStr(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function sortLocations(items: LocationItem[]): LocationItem[] {
  return [...items].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) {
      return a.sortOrder - b.sortOrder;
    }

    return a.name.localeCompare(b.name);
  });
}

/* =====================================================
   COUNTIES
===================================================== */

export async function listCounties(db: Firestore): Promise<LocationItem[]> {
  const q = query(
    collection(db, "counties"),
    where("isDeleted", "==", false),
    orderBy("sortOrder", "asc")
  );

  const snap = await getDocs(q);

  return sortLocations(
    snap.docs.map((docSnap) => {
      const data = docSnap.data() as Record<string, unknown>;

      return {
        id: docSnap.id,
        name: asStr(data.name),
        nameNormalized: asStr(data.nameNormalized),
        sortOrder: asNum(data.sortOrder, 0),
        isDeleted: asBool(data.isDeleted, false),
      };
    })
  );
}

/* =====================================================
   CITIES
===================================================== */

export async function listCitiesByCounty(
  db: Firestore,
  countyId: string
): Promise<LocationItem[]> {
  const cleanCountyId = String(countyId || "").trim();

  if (!cleanCountyId) {
    return [];
  }

  const q = query(
    collection(db, "counties", cleanCountyId, "cities"),
    where("isDeleted", "==", false),
    orderBy("sortOrder", "asc")
  );

  const snap = await getDocs(q);

  return sortLocations(
    snap.docs.map((docSnap) => {
      const data = docSnap.data() as Record<string, unknown>;

      return {
        id: docSnap.id,
        name: asStr(data.name),
        nameNormalized: asStr(data.nameNormalized),
        sortOrder: asNum(data.sortOrder, 0),
        isDeleted: asBool(data.isDeleted, false),
        countyId: asStr(data.countyId, cleanCountyId),
        countyName: asStr(data.countyName),
      };
    })
  );
}

/* =====================================================
   AREAS
===================================================== */

export async function listAreasByCity(
  db: Firestore,
  countyId: string,
  cityId: string
): Promise<LocationItem[]> {
  const cleanCountyId = String(countyId || "").trim();
  const cleanCityId = String(cityId || "").trim();

  if (!cleanCountyId || !cleanCityId) {
    return [];
  }

  const q = query(
    collection(db, "counties", cleanCountyId, "cities", cleanCityId, "areas"),
    where("isDeleted", "==", false),
    orderBy("sortOrder", "asc")
  );

  const snap = await getDocs(q);

  return sortLocations(
    snap.docs.map((docSnap) => {
      const data = docSnap.data() as Record<string, unknown>;

      return {
        id: docSnap.id,
        name: asStr(data.name),
        nameNormalized: asStr(data.nameNormalized),
        sortOrder: asNum(data.sortOrder, 0),
        isDeleted: asBool(data.isDeleted, false),
        countyId: asStr(data.countyId, cleanCountyId),
        countyName: asStr(data.countyName),
        cityId: asStr(data.cityId, cleanCityId),
        cityName: asStr(data.cityName),
      };
    })
  );
}